// FotoPessoa.js — o rosto de uma pessoa (autor, leitor, assinante) na tela.
//
// POR QUE EXISTE:
// o site desenhava avatar em cinco lugares, cada um com a sua própria versão da
// mesma regra ("qual campo é a foto?", "e se não tiver?", "e se a imagem
// quebrar?"). Três deles caíam numa inicial cinza; dois num monograma colorido;
// nenhum tratava imagem quebrada. O resultado é o tipo de divergência que só
// aparece na tela: o mesmo autor com rosto na home e com letra no comentário.
//
// DE ONDE VEM A FOTO (a ordem importa, e é decidida no backend):
//   1. a que a pessoa enviou (`image`);
//   2. a do currículo Lattes, importada pelo painel (`foto_lattes`).
// O backend já resolve as duas em `foto` — ver o accessor `getFotoAttribute`
// em UsuarioEditora. Aqui a ordem é repetida só para o componente aguentar
// receber um objeto cru de qualquer endpoint mais antigo.
//
// A FOTO DO LATTES É PADRÃO, NÃO ESCOLHA: se a pessoa enviar a dela depois, a
// dela ganha sozinha; se apagar, a do Lattes volta. Nada aqui precisa saber
// disso — é só a consequência da ordem acima.
//
// SEM FOTO, A MARCA DA INSIGNE (18/10/2026, pedido do dono): quem não tem foto
// (ou tem uma que quebrou) aparece com a marca da editora num disco laranja,
// o mesmo desenho da Avaliação Editorial. Antes era um monograma com as
// iniciais num degradê tirado do nome.
import React, { useState } from "react";
import MarcaPadrao from "./MarcaPadrao";

const API_URL = process.env.REACT_APP_API;

// "Kelly Cristina Ramos" -> "KR". Duas letras já parecem um monograma
// desenhado; uma letra sozinha parecia cadastro pela metade.
export const iniciaisDe = (nome) => {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

// URL final da foto: aceita URL completa ou caminho do storage público.
export const urlDaFoto = (pessoa) => {
  const f =
    (typeof pessoa === "string" ? pessoa : null) ||
    pessoa?.foto ||
    pessoa?.usuario_foto ||
    pessoa?.image ||
    pessoa?.foto_lattes ||
    pessoa?.imagem ||
    pessoa?.avatar ||
    null;
  if (!f) return null;
  return /^https?:\/\//i.test(f) ? f : `${API_URL}public/${f}`;
};

/**
 * @param {object|string} pessoa  objeto do usuário/autor, ou a URL direta
 * @param {string} nome           usado no alt e no monograma
 * @param {string} tamanho        classes de tamanho (padrão: w-16 h-16)
 * @param {string} className      classes extras no wrapper
 */
const FotoPessoa = ({
  pessoa,
  nome,
  tamanho = "w-16 h-16",
  className = "",
  titulo,
}) => {
  const [quebrou, setQuebrou] = useState(false);
  const src = quebrou ? null : urlDaFoto(pessoa);
  const rotulo = nome || pessoa?.nome || pessoa?.usuario_nome || "Usuário";

  const base = `${tamanho} shrink-0 rounded-full object-cover border border-line bg-insigne-navy-deep ${className}`;

  if (!src) {
    return (
      <div className={`${tamanho} shrink-0 rounded-full overflow-hidden ${className}`}>
        <MarcaPadrao titulo={titulo || rotulo} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={rotulo}
      title={titulo || rotulo}
      loading="lazy"
      // Imagem quebrada não pode virar o ícone de arquivo faltando do
      // navegador: cai na marca da Insigne, que é um estado apresentável.
      onError={() => setQuebrou(true)}
      className={base}
    />
  );
};

export default FotoPessoa;
